import React, { useMemo, useState, useEffect } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Box, useTheme, Typography, IconButton, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { selectReports } from "../../Redux/Reducers/selectors";

const HistoricalReportsTable = ({ reportId, onViewReportData }) => {
  const theme = useTheme();
  const reports = useSelector(selectReports);
  const [pageSize, setPageSize] = useState(100);
  const [sortModel, setSortModel] = useState([
    {
      field: 'date',
      sort: 'desc'
    }
  ]);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every 30 seconds to re-evaluate status
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Get the specific report and its generations
  const reportData = useMemo(() => {
    if (!reports || !reportId) return [];
    
    const findReport = reports.find((i) => {
      const reportIdFromState = i.reportId;
      const searchId = reportId;
      return String(reportIdFromState) === String(searchId);
    });

    if (!findReport || !findReport.generations) return [];

    // Helper function to determine effective status
    const getEffectiveStatus = (stage, date) => {
      // If stage is 2 (Started) and older than or equal to 15 minutes, treat as Failed
      if (stage === 2 && date) {
        const reportDate = moment(date);
        const now = moment();
        const minutesDiff = now.diff(reportDate, 'minutes');
        
        // Debug: log the time difference for stage 2 reports
        
        if (minutesDiff >= 15) {
          return 3; // Failed
        }
      }
      return stage;
    };

    const processedData = findReport.generations.map((generation, index) => {
      const effectiveStage = getEffectiveStatus(generation.stage, generation.date);
      
      const row = {
        id: `${generation.reportId}-${generation.date}-${index}`, // Create unique ID
        date: generation.date,
        stage: generation.stage,
        effectiveStage: effectiveStage, // Add effective stage for display
        reportName: findReport.name,
        // Don't spread generation here as it can overwrite the properties above
        // Only add the specific properties we need
        file: generation.file,
        duration: generation.duration,
        expiresAt: generation.expiresAt,
        user: generation.user,
        managerId: generation.managerId,
        reportId: generation.reportId,
      };
   
      
      return row;
    });

    // Sort by date in descending order (newest first)
    const sortedData = processedData.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
    
    return sortedData;
  }, [reports, reportId, currentTime]);

  const columns = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (!value) return null;
        const m = moment(value);
        return m.isValid() ? m.valueOf() : null; // Return timestamp for proper sorting
      },
      renderCell: (params) => {
        if (!params.row.date) return "-";
        const formattedDate = moment(params.row.date).format("MMM DD, YYYY");
        return (
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {formattedDate}
          </Box>
        );
      },
    },
    {
      field: "time",
      headerName: "Time",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => {
        if (!row.date) return null;
        const m = moment(row.date);
        return m.isValid() ? m.valueOf() : null; // Return timestamp for proper sorting
      },
      renderCell: (params) => {
        if (!params.row.date) return "-";
        const formattedTime = moment(params.row.date).format("hh:mm A");
        return (
          <Box sx={{ color: theme.palette.text.secondary }}>
            {formattedTime}
          </Box>
        );
      },
    },
    {
      field: "effectiveStage",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => {
        const effectiveStage = row.effectiveStage;
        if (effectiveStage === 0) return "Completed";
        if (effectiveStage === 1) return "Processing";
        if (effectiveStage === 2) return "Started";
        if (effectiveStage === 3) return "Failed";
        return "-";
      },
      renderCell: (params) => {
        const effectiveStage = params.row.effectiveStage;
        let color = theme.palette.text.secondary;
        
        if (effectiveStage === 0) color = theme.palette.success.main;
        else if (effectiveStage === 1) color = theme.palette.warning.main;
        else if (effectiveStage === 2) color = theme.palette.info.main;
        else if (effectiveStage === 3) color = theme.palette.error.main;
        return (
          <Box sx={{ 
            fontWeight: 500, 
            color: color,
            textTransform: 'capitalize'
          }}>
            {params.value || "-"}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", width: "100%" }}>
          <Tooltip title="View Report Data">
            <IconButton
              color="primary"
              size="small"
              onClick={() => onViewReportData(params.row)}
              sx={{
                height: "32px",
                width: "32px",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  const rows = reportData; // Use reportData directly since we already have unique IDs

  // Show empty state if no data
  if (!reportId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Report Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select a report to view its historical data.
        </Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No Historical Data Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This report doesn't have any generation history yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <DataGridPro
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
        pagination
        disableSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        sx={{
          border: "none",
          // height: "calc(100vh - 20px)",
          height:"100%",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default HistoricalReportsTable; 